import type { PeerStatus } from '../lib/peer.ts';
import type { SignalingStatus } from '../lib/signaling.ts';
import type { TranslatorState } from '@voxa/shared';
import { useT, type Dictionary } from '../i18n/index.tsx';

type Tone = 'ok' | 'warn' | 'bad' | '';

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return <span className={`pill ${tone}`}>{children}</span>;
}

const SIGNALING: Record<SignalingStatus, [Tone, keyof Dictionary['status']]> = {
  connecting: ['warn', 'serverConnecting'],
  open: ['ok', 'serverOpen'],
  reconnecting: ['warn', 'serverReconnecting'],
  closed: ['bad', 'serverClosed'],
};

const PEER: Record<PeerStatus, [Tone, keyof Dictionary['status']]> = {
  new: ['', 'peerWaiting'],
  connecting: ['warn', 'peerConnecting'],
  connected: ['ok', 'peerConnected'],
  reconnecting: ['warn', 'peerReconnecting'],
  failed: ['bad', 'peerFailed'],
  closed: ['', 'peerGone'],
};

const TRANSLATOR: Record<TranslatorState, [Tone, keyof Dictionary['status']]> = {
  idle: ['', 'translatorIdle'],
  connecting: ['warn', 'translatorConnecting'],
  ready: ['ok', 'translatorReady'],
  asleep: ['', 'translatorAsleep'],
  reconnecting: ['warn', 'translatorReconnecting'],
  failed: ['bad', 'translatorFailed'],
  closed: ['', 'translatorClosed'],
};

export function StatusPills({
  signaling,
  peer,
  translator,
  peerTranslator,
  relayed,
}: {
  signaling: SignalingStatus;
  peer: PeerStatus;
  translator: TranslatorState;
  peerTranslator: TranslatorState;
  relayed: boolean;
}) {
  const t = useT();
  const [sTone, sKey] = SIGNALING[signaling];
  const [pTone, pKey] = PEER[peer];
  const [tTone, tKey] = TRANSLATOR[translator];
  const peerReady = peerTranslator === 'ready';

  return (
    <div className="statuses">
      <Pill tone={sTone}>{t.status[sKey]}</Pill>
      <Pill tone={pTone}>{t.status[pKey]}</Pill>
      <Pill tone={tTone}>{t.status[tKey]}</Pill>
      {/* The peer's translator matters: the call clock waits for both. */}
      <Pill tone={peerReady ? 'ok' : ''}>
        {peerReady
          ? t.status.peerTranslatorReady
          : peerTranslator === 'asleep'
            ? t.status.peerTranslatorAsleep
            : t.status.peerTranslatorWaiting}
      </Pill>
      {relayed && <Pill tone="warn">{t.status.relayed}</Pill>}
    </div>
  );
}
